import type { LocationData, GrandSnatchState } from './types';
import { decideWinner } from './scoring';

// A Location is the shared scenario of a match, so its scoring rule must be
// GENERIC — never tied to a specific suit. That way any Location is valid
// whatever the two players drafted (no need to check drafted suits at draft
// end). Generic kinds: cards_count, total_resources, value_count. Suit-bound
// goals belong to Missions instead (see missions.ts).
//
// 16 locations covering every generic rule config: card-count and
// total-resources (forward + reverse) plus value_count for each value 1-6
// (forward + reverse). "Reverse" goals are coherent here because a Location
// applies symmetrically to both players.
export const LOCATION_DATABASE: LocationData[] = [
  // --- card count ---
  {
    id: 'all_mine',
    name: "It's All Mine!",
    description: 'Grab the most cards of all',
    rule: { kind: 'cards_count' },
    winScore: 1,
    background: 'res://globals/assets/backgrounds/bg_sea_1.png'
  },
  {
    id: 'frugality',
    name: 'Frugality Wins',
    description: 'End with the fewest cards',
    rule: { kind: 'cards_count', reverse: true },
    winScore: 1,
    background: 'res://globals/assets/backgrounds/bg_sea_1.png'
  },

  // --- total resources ---
  {
    id: 'the_big_score',
    name: 'The Big Score',
    description: 'Win the highest total resources across all suits',
    rule: { kind: 'total_resources' },
    winScore: 2,
    background: 'res://globals/assets/backgrounds/bg_black_1.png'
  },
  {
    id: 'keep_it_low',
    name: 'Keep It Low',
    description: 'End with the lowest total resources',
    rule: { kind: 'total_resources', reverse: true },
    winScore: 1,
    background: 'res://globals/assets/backgrounds/bg_sea_1.png'
  },

  // --- value 1 ---
  {
    id: 'addicted_one',
    name: 'Addicted to One',
    description: 'Collect the most n. 1 value cards',
    rule: { kind: 'value_count', value: 1 },
    winScore: 1,
    background: 'res://globals/assets/backgrounds/bg_colors_1.png'
  },
  {
    id: 'above_pennies',
    name: 'Above the Pennies',
    description: 'End with the fewest n. 1 value cards',
    rule: { kind: 'value_count', value: 1, reverse: true },
    winScore: 1,
    background: 'res://globals/assets/backgrounds/bg_colors_1.png'
  },

  // --- value 2 ---
  {
    id: 'deuces_take',
    name: 'Deuces Take It',
    description: 'Collect the most n. 2 value cards',
    rule: { kind: 'value_count', value: 2 },
    winScore: 1,
    background: 'res://globals/assets/backgrounds/bg_colors_1.png'
  },
  {
    id: 'drop_deuces',
    name: 'Drop the Deuces',
    description: 'End with the fewest n. 2 value cards',
    rule: { kind: 'value_count', value: 2, reverse: true },
    winScore: 1,
    background: 'res://globals/assets/backgrounds/bg_colors_1.png'
  },

  // --- value 3 ---
  {
    id: 'middle_management',
    name: 'Middle Management',
    description: 'Collect the most n. 3 value cards',
    rule: { kind: 'value_count', value: 3 },
    winScore: 1,
    background: 'res://globals/assets/backgrounds/bg_colors_1.png'
  },
  {
    id: 'no_loose_threes',
    name: 'No Loose Threes',
    description: 'End with the fewest n. 3 value cards',
    rule: { kind: 'value_count', value: 3, reverse: true },
    winScore: 1,
    background: 'res://globals/assets/backgrounds/bg_colors_1.png'
  },

  // --- value 4 ---
  {
    id: 'four_square',
    name: 'Four-Square',
    description: 'Collect the most n. 4 value cards',
    rule: { kind: 'value_count', value: 4 },
    winScore: 1,
    background: 'res://globals/assets/backgrounds/bg_colors_1.png'
  },
  {
    id: 'skip_the_fours',
    name: 'Skip the Fours',
    description: 'End with the fewest n. 4 value cards',
    rule: { kind: 'value_count', value: 4, reverse: true },
    winScore: 1,
    background: 'res://globals/assets/backgrounds/bg_colors_1.png'
  },

  // --- value 5 ---
  {
    id: 'high_fives',
    name: 'High Fives',
    description: 'Collect the most n. 5 value cards',
    rule: { kind: 'value_count', value: 5 },
    winScore: 2,
    background: 'res://globals/assets/backgrounds/bg_colors_1.png'
  },
  {
    id: 'dodge_the_fives',
    name: 'Dodge the Fives',
    description: 'End with the fewest n. 5 value cards',
    rule: { kind: 'value_count', value: 5, reverse: true },
    winScore: 1,
    background: 'res://globals/assets/backgrounds/bg_colors_1.png'
  },

  // --- value 6 ---
  {
    id: 'top_brass',
    name: 'Top Brass',
    description: 'Collect the most n. 6 value cards',
    rule: { kind: 'value_count', value: 6 },
    winScore: 2,
    background: 'res://globals/assets/backgrounds/bg_colors_1.png'
  },
  {
    id: 'mind_the_sixes',
    name: 'Mind the Sixes',
    description: 'End with the fewest n. 6 value cards',
    rule: { kind: 'value_count', value: 6, reverse: true },
    winScore: 1,
    background: 'res://globals/assets/backgrounds/bg_colors_1.png'
  }
];

export function calculateLocationWinner(G: GrandSnatchState): string | null {
  const loc = G.activeLocation;
  if (!loc || !loc.rule) return null; // tolerate matches saved under the legacy LocationData shape
  return decideWinner(
    loc.rule,
    { discard: G.players['0'].discard, resources: G.players['0'].resources },
    { discard: G.players['1'].discard, resources: G.players['1'].resources },
  );
}
